import {
  Badge,
  Checkbox,
  Flex,
  IconButton,
  Link,
  Menu,
  MenuButton,
  MenuList,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr
} from '@chakra-ui/react'
import { useEffect, useState } from 'react'
import { Link as RouterLink, useSearchParams } from 'react-router-dom'

import { ActionButton, ActionFab, ActionMenuItem } from '../../../components/ActionButton'
import { ConfirmDialog } from '../../../components/ConfirmDialog'
import { EmptyCell } from '../../../components/EmptyCell'
import { FilterBar, FilterDateRange, FilterField, FilterSearch, FilterSelect } from '../../../components/FilterBar'
import { IFilterDateRangeValue } from '../../../components/FilterBar/FilterDateRange'
import { Icon } from '../../../components/Icon'
import { TableSkeletonRows } from '../../../components/Loading'
import { PageTitle } from '../../../components/PageTitle'
import Paginator, { DEFAULT_PAGE_SIZE } from '../../../components/paginator'
import { ReasonModal } from '../../../components/ReasonModal'
import { SearchableSelect } from '../../../components/Select'
import { StatusBadge, statusOptions } from '../../../components/StatusBadge'
import { TableEmptyRow, TableErrorRow } from '../../../components/TableEmptyRow'
import { Tooltip } from '../../../components/Tooltip'
import { useUser } from '../../../contexts/UserContext'
import { formatApiDate, useAppToast, useHasRole } from '../../../helpers'
import { JobPositionService } from '../../../services/JobPosition/JobPositionService'
import { JobRequisitionService } from '../../../services/JobRequisition/JobRequisitionService'
import { ContractType, JobRequisition, JobRequisitionStatus } from '../../../services/JobRequisition/types'
import { SectorService } from '../../../services/Sector/SectorService'
import {
  CONTRACT_TYPE_LABELS,
  CONTRACT_TYPE_OPTIONS,
  DEFAULT_STATUS_FILTER,
  JOB_REQUISITION_STATUS,
  REASON_MAX_LENGTH
} from './constants'
import { JobRequisitionCloseModal } from './JobRequisitionCloseModal'
import { JobRequisitionFormModal } from './JobRequisitionFormModal'
import { businessDaysLabel } from './JobRequisitionParts'
import { JobRequisitionSiteModal } from './JobRequisitionSiteModal'
import { JobRequisitionViewModal } from './JobRequisitionViewModal'

interface IFilters {
  status: JobRequisitionStatus[]
  sectorId: string
  jobPositionId: number | null
  contractType: string
  opened: IFilterDateRangeValue
  number: string
  toSign: boolean
}

const INITIAL_FILTERS: IFilters = {
  status: DEFAULT_STATUS_FILTER,
  sectorId: '',
  jobPositionId: null,
  contractType: '',
  opened: { start: '', end: '' },
  number: '',
  toSign: false
}

const COLUMNS = 13

const STATUS_OPTIONS = statusOptions(JOB_REQUISITION_STATUS) as { value: JobRequisitionStatus; label: string }[]

type ModalKind = 'form' | 'site' | 'close' | 'cancel' | 'delete'

/**
 * "Vagas de Emprego": the RPs opened from MPs. Someone who can only sign ("Assinar RP") sees just
 * the RPs they have to sign; the bell and the e-mail open one through `?rp={id}`.
 */
export default function JobRequisitionsPage() {
  const service = new JobRequisitionService()
  const toast = useAppToast()
  const hasRole = useHasRole()
  const { id: userId } = useUser()
  const [searchParams, setSearchParams] = useSearchParams()
  const [requisitions, setRequisitions] = useState<JobRequisition[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [filters, setFilters] = useState<IFilters>(INITIAL_FILTERS)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [totalPages, setTotalPages] = useState(1)
  const [sectorOptions, setSectorOptions] = useState<{ value: number; label: string }[]>([])
  const [roleOptions, setRoleOptions] = useState<{ value: number; label: string }[]>([])
  const [viewingId, setViewingId] = useState<number | null>(null)
  const [openModal, setOpenModal] = useState<ModalKind | null>(null)
  const [target, setTarget] = useState<JobRequisition | null>(null)
  const permissions = {
    add: hasRole('add_job_requisitions'),
    edit: hasRole('edit_job_requisitions'),
    cancel: hasRole('cancel_job_requisitions'),
    delete: hasRole('delete_job_requisitions'),
    sign: hasRole('sign_job_requisitions'),
    viewTalents: hasRole('view_talents')
  }

  useEffect(() => {
    new SectorService()
      .listForSelect()
      .then((sectors) => setSectorOptions(sectors.map((sector) => ({ value: sector.id, label: sector.name }))))
      .catch((error) => toast.error('Falha ao carregar setores', error))
    new JobPositionService()
      .listAll()
      .then((roles) => setRoleOptions(roles.map((role) => ({ value: role.id, label: role.name }))))
      .catch((error) => toast.error('Erro ao carregar os cargos', error))
  }, [])

  useEffect(() => {
    refreshList()
  }, [filters, page, pageSize])

  // Deep link from the bell and the e-mails: /vagas-de-emprego?rp={id}
  useEffect(() => {
    const requisitionParam = Number(searchParams.get('rp'))
    if (requisitionParam) setViewingId(requisitionParam)
  }, [searchParams])

  const refreshList = () => {
    setLoading(true)
    setLoadError(false)
    service
      .list({
        status: filters.status,
        sector_id: filters.sectorId ? Number(filters.sectorId) : undefined,
        job_position_id: filters.jobPositionId ?? undefined,
        contract_type: (filters.contractType || undefined) as ContractType | undefined,
        opened_from: filters.opened.start || undefined,
        opened_to: filters.opened.end || undefined,
        number: /^\d+$/.test(filters.number) ? Number(filters.number) : undefined,
        to_sign: filters.toSign || undefined,
        page,
        per_page: pageSize
      })
      .then((result) => {
        setRequisitions(result.data)
        setTotalPages(result.last_page)
      })
      .catch((error) => {
        setLoadError(true)
        toast.error('Erro ao carregar as vagas', error)
      })
      .finally(() => setLoading(false))
  }

  const updateFilters = (patch: Partial<IFilters>) => {
    setFilters((prev) => ({ ...prev, ...patch }))
    setPage(1)
  }

  const isDirty = JSON.stringify(filters) !== JSON.stringify(INITIAL_FILTERS)

  const open = (kind: ModalKind, requisition: JobRequisition | null) => {
    setTarget(requisition)
    setOpenModal(kind)
  }

  const closeModal = () => setOpenModal(null)

  const closeView = () => {
    setViewingId(null)
    if (searchParams.has('rp')) setSearchParams({}, { replace: true })
  }

  const handleCancel = (reason: string) => {
    if (!target) return Promise.resolve()

    return service
      .cancel(target.id, reason)
      .then(() => {
        toast.success(`RP ${target.number} cancelada`)
        refreshList()
      })
      .catch((error) => {
        toast.error('Erro ao cancelar a RP', error)
        throw error
      })
  }

  const handleDelete = () => {
    if (!target) return Promise.resolve()

    return service
      .delete(target.id)
      .then(() => {
        toast.success(`RP ${target.number} excluída`)
        closeModal()
        refreshList()
      })
      .catch((error) => toast.error('Erro ao excluir a RP', error))
  }

  const isMySignature = (requisition: JobRequisition) =>
    permissions.sign && requisition.status === 'pending_validation' && Number(userId) === requisition.signer_user_id

  return (
    <>
      <PageTitle title="Vagas de Emprego" description="Requisições de Pessoal (RP) abertas a partir das MPs" />
      <FilterBar onClear={() => updateFilters(INITIAL_FILTERS)} isDirty={isDirty}>
        <FilterSearch
          label="Nº RP"
          value={filters.number}
          onChange={(number) => updateFilters({ number })}
          placeholder="Buscar pelo número"
        />
        <FilterField label="Status" flex="2 1 280px" maxW="420px">
          <SearchableSelect<JobRequisitionStatus>
            isMulti
            size="sm"
            options={STATUS_OPTIONS}
            value={filters.status}
            onChange={(status) => updateFilters({ status })}
            placeholder="Todos"
            entity="status"
          />
        </FilterField>
        <FilterSelect
          label="Setor"
          value={filters.sectorId}
          onChange={(sectorId) => updateFilters({ sectorId })}
          options={sectorOptions}
        />
        <FilterField label="Cargo">
          <SearchableSelect<number>
            size="sm"
            isClearable
            options={roleOptions}
            value={filters.jobPositionId ?? ''}
            onChange={(jobPositionId) => updateFilters({ jobPositionId: jobPositionId === '' ? null : jobPositionId })}
            placeholder="Todos"
            entity="cargo"
          />
        </FilterField>
        <FilterSelect
          label="Regime"
          value={filters.contractType}
          onChange={(contractType) => updateFilters({ contractType })}
          options={CONTRACT_TYPE_OPTIONS}
        />
        <FilterDateRange label="Abertura" value={filters.opened} onChange={(opened) => updateFilters({ opened })} />
        {permissions.sign && (
          <FilterField label="Assinatura" flex="0 1 auto">
            {/* `minH` do tamanho de um controle `sm`, alinhando o checkbox com os campos vizinhos. */}
            <Flex alignItems="center" minH="32px">
              <Checkbox isChecked={filters.toSign} onChange={(e) => updateFilters({ toSign: e.target.checked })}>
                RPs para assinar
              </Checkbox>
            </Flex>
          </FilterField>
        )}
      </FilterBar>

      <TableContainer>
        <Table>
          <Thead>
            <Tr>
              <Th>Nº RP</Th>
              <Th>Cargo</Th>
              <Th>Setor</Th>
              <Th>Regime</Th>
              <Th>Áreas</Th>
              <Th>Portfólio</Th>
              {/* Two-line headers: thirteen columns, and these four hold short dates. */}
              <Th whiteSpace="normal">Data de Abertura</Th>
              <Th whiteSpace="normal">Prazo de Fechamento</Th>
              <Th whiteSpace="normal">Data de Fechamento</Th>
              <Th whiteSpace="normal">Tempo de Fechamento</Th>
              <Th>Candidatos</Th>
              <Th>Status</Th>
              <Th>Ações</Th>
            </Tr>
          </Thead>
          <Tbody>
            {loading ? (
              <TableSkeletonRows cols={COLUMNS} />
            ) : loadError ? (
              <TableErrorRow colSpan={COLUMNS} entity="vagas" feminine onRetry={refreshList} />
            ) : requisitions.length === 0 ? (
              <TableEmptyRow colSpan={COLUMNS} entity="vaga" feminine />
            ) : (
              requisitions.map((requisition) => {
                const canEdit =
                  permissions.edit && (requisition.status === 'pending_validation' || requisition.status === 'open')
                const canClose = permissions.edit && requisition.status === 'open'
                const canCancel =
                  permissions.cancel && (requisition.status === 'pending_validation' || requisition.status === 'open')
                const canDelete = permissions.delete && requisition.status === 'pending_validation'

                return (
                  // An open RP past its deadline is highlighted, as the client's document asks.
                  <Tr key={requisition.id} {...(requisition.is_overdue ? { bg: 'red.50' } : {})}>
                    <Td>{requisition.number}</Td>
                    <Td whiteSpace="normal" minW="160px">
                      {requisition.job_position.name}
                    </Td>
                    <Td>
                      <EmptyCell value={requisition.sector?.name} />
                    </Td>
                    <Td>{CONTRACT_TYPE_LABELS[requisition.contract_type]}</Td>
                    <Td whiteSpace="normal" minW="140px">
                      <EmptyCell value={requisition.areas?.join(', ')} />
                    </Td>
                    <Td>{requisition.allow_portfolio_link ? 'Sim' : 'Não'}</Td>
                    <Td>
                      <EmptyCell value={formatApiDate(requisition.opened_at)} />
                    </Td>
                    <Td>
                      <EmptyCell value={formatApiDate(requisition.deadline)} />
                      {requisition.is_overdue && (
                        <Badge ml={2} colorScheme="red">
                          Vencido
                        </Badge>
                      )}
                    </Td>
                    <Td>
                      <EmptyCell value={formatApiDate(requisition.closed_at)} />
                    </Td>
                    <Td>
                      <EmptyCell
                        value={
                          requisition.closing_business_days !== null
                            ? businessDaysLabel(requisition.closing_business_days)
                            : null
                        }
                      />
                    </Td>
                    <Td>
                      {/* Opens the talent bank already filtered by this RP. */}
                      {permissions.viewTalents && requisition.applications_count > 0 ? (
                        <Link as={RouterLink} to={`/job-applications?rp=${requisition.id}`} color="blue.500">
                          {requisition.applications_count}
                        </Link>
                      ) : (
                        requisition.applications_count
                      )}
                    </Td>
                    <Td>
                      <Tooltip
                        label={`Aguardando a assinatura de ${requisition.signer.name}`}
                        isDisabled={requisition.status !== 'pending_validation'}
                      >
                        <StatusBadge status={requisition.status} statuses={JOB_REQUISITION_STATUS} />
                      </Tooltip>
                    </Td>
                    <Td>
                      {isMySignature(requisition) ? (
                        <ActionButton
                          action="sign"
                          label="Analisar e assinar"
                          onClick={() => setViewingId(requisition.id)}
                        />
                      ) : (
                        <ActionButton action="view" label="Ver RP" onClick={() => setViewingId(requisition.id)} />
                      )}
                      {canEdit && (
                        <ActionButton
                          action="edit"
                          label={requisition.status === 'open' ? 'Editar áreas e portfólio' : 'Editar'}
                          onClick={() => open(requisition.status === 'open' ? 'site' : 'form', requisition)}
                        />
                      )}
                      {(canClose || canCancel || canDelete) && (
                        <Menu isLazy>
                          <MenuButton as={IconButton} aria-label="Mais ações" size="sm" variant="ghost">
                            <Icon name="lni lni-line-dotted" transform="rotate(90deg)" />
                          </MenuButton>
                          <MenuList>
                            {canClose && (
                              <ActionMenuItem
                                action="conclude"
                                label="Fechar vaga"
                                onClick={() => open('close', requisition)}
                              />
                            )}
                            {canCancel && (
                              <ActionMenuItem
                                action="cancel"
                                label="Cancelar RP"
                                onClick={() => open('cancel', requisition)}
                              />
                            )}
                            {canDelete && (
                              <ActionMenuItem action="delete" onClick={() => open('delete', requisition)} />
                            )}
                          </MenuList>
                        </Menu>
                      )}
                    </Td>
                  </Tr>
                )
              })
            )}
          </Tbody>
        </Table>
      </TableContainer>

      <Paginator
        page={page}
        totalPages={totalPages}
        changePage={setPage}
        pageSize={pageSize}
        onPageSizeChange={(size) => {
          setPageSize(size)
          setPage(1)
        }}
      />

      {permissions.add && <ActionFab label="Nova RP" onClick={() => open('form', null)} />}

      <JobRequisitionViewModal
        isOpen={viewingId !== null}
        requisitionId={viewingId}
        onClose={closeView}
        onChanged={refreshList}
      />
      <JobRequisitionFormModal
        isOpen={openModal === 'form'}
        requisitionId={target?.id}
        onClose={closeModal}
        onSaved={refreshList}
      />
      <JobRequisitionSiteModal
        isOpen={openModal === 'site'}
        requisition={target}
        onClose={closeModal}
        onSaved={refreshList}
      />
      <JobRequisitionCloseModal
        isOpen={openModal === 'close'}
        requisition={target}
        onClose={closeModal}
        onClosed={refreshList}
      />
      <ReasonModal
        isOpen={openModal === 'cancel'}
        onClose={closeModal}
        title={`Cancelar RP ${target?.number ?? ''}`}
        label="Motivo do cancelamento"
        description="A RP cancelada não volta e, se estiver em aberto, sai do site na hora."
        confirmLabel="Cancelar RP"
        cancelLabel="Voltar"
        maxLength={REASON_MAX_LENGTH}
        onConfirm={handleCancel}
      />
      {target && (
        <ConfirmDialog
          isOpen={openModal === 'delete'}
          onClose={closeModal}
          onConfirm={handleDelete}
          action="delete"
          entity="RP"
          feminine
          name={String(target.number)}
          warning="Só uma RP que ainda não foi assinada pode ser excluída."
        />
      )}
    </>
  )
}
